import { ClassificationGroup } from "types/Models/classification";
import { emptyApi } from "./emptyApi";

export const classificationApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getAllClassification: build.query<
      ClassificationGroup[],
      { entityId: string }
    >({
      query: ({ entityId }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/classifications/`,
        };
      },
      providesTags: () => [{ type: "CLASSIFICATION" }],
    }),

    createClassification: build.mutation<
      ClassificationGroup,
      {
        entityId: string;
        payload: {
          group_name: string;
          class_names: string[];
        };
      }
    >({
      query: ({ entityId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/classifications/`,
          method: "POST",
          body: {
            name: payload.group_name,
            class_names: payload.class_names,
          },
        };
      },
      invalidatesTags: [{ type: "CLASSIFICATION" }],
    }),

    getTxnClassifications: build.query<
      ClassificationGroup[],
      {
        entityId: string;
        transactionId: string;
      }
    >({
      query: ({ entityId, transactionId }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/transaction/${transactionId}/classifications/`,
        };
      },
      providesTags: (result, error, { transactionId }) => [
        { type: "TXN_CLASSIFICATION", id: transactionId },
      ],
    }),

    addTxnClassification: build.mutation<
      ClassificationGroup[],
      {
        entityId: string;
        transactionId: string;
        payload: {
          classification_id: string;
        };
      }
    >({
      query: ({ entityId, transactionId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/transaction/${transactionId}/classifications/`,
          method: "PATCH",
          body: {
            classification_id: payload.classification_id,
          },
        };
      },
      invalidatesTags: (result, error, { transactionId }) => [
        "BOOKS_TRANSACTIONS",
        { type: "TXN_CLASSIFICATION", id: transactionId },
      ],
    }),

    deleteTxnClassification: build.mutation<
      ClassificationGroup[],
      {
        entityId: string;
        transactionId: string;
        classificationId: string;
      }
    >({
      query: ({ entityId, transactionId, classificationId }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/transaction/${transactionId}/classification/${classificationId}/`,
          method: "DELETE",
        };
      },
      invalidatesTags: (result, error, { transactionId }) => [
        "BOOKS_TRANSACTIONS",
        { type: "TXN_CLASSIFICATION", id: transactionId },
      ],
    }),

    confirmTxnClassification: build.mutation<
      ClassificationGroup[],
      {
        entityId: string;
        transactionId: string;
        classificationId: string;
      }
    >({
      query: ({ entityId, transactionId, classificationId }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/transaction/${transactionId}/classifications/`,
          method: "PATCH",
          body: {
            classification_id: classificationId,
            confirm: true,
          },
        };
      },
      invalidatesTags: (result, error, { transactionId }) => [
        "BOOKS_TRANSACTIONS",
        { type: "TXN_CLASSIFICATION", id: transactionId },
      ],
    }),

    rejectTxnClassification: build.mutation<
      ClassificationGroup[],
      {
        entityId: string;
        transactionId: string;
        classificationId: string;
      }
    >({
      query: ({ entityId, transactionId, classificationId }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/transaction/${transactionId}/classifications/`,
          method: "PATCH",
          body: {
            classification_id: classificationId,
            reject: true,
          },
        };
      },
      invalidatesTags: (result, error, { transactionId }) => [
        "BOOKS_TRANSACTIONS",
        { type: "TXN_CLASSIFICATION", id: transactionId },
      ],
    }),

    bulkAssignClassification: build.mutation<
      ClassificationGroup[],
      {
        entityId: string;
        payload: {
          class_ids: string[];
          transaction_ids: string[];
        };
      }
    >({
      query: ({ entityId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/bulk_transaction_classifications/`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: [
        "TXN_CLASSIFICATION",
        "BOOKS_TRANSACTIONS",
        "LIVE_REPORT",
      ],
    }),

    bulkConfirmAIClassifications: build.mutation<
      { confirmed_count: number },
      {
        entityId: string;
        payload: {
          transaction_ids: string[];
        };
      }
    >({
      query: ({ entityId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/bulk_confirm_classifications/`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: [
        "TXN_CLASSIFICATION",
        "BOOKS_TRANSACTIONS",
        "LIVE_REPORT",
      ],
    }),

    updateClassification: build.mutation<
      ClassificationGroup,
      {
        entityId: string;
        payload: {
          class_id?: string;
          group_id?: string;
          name: string;
        };
      }
    >({
      query: ({ entityId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/classifications/`,
          method: "PUT",
          body: payload,
        };
      },
      invalidatesTags: ["CLASSIFICATION", "TXN_CLASSIFICATION"],
    }),

    deleteClassification: build.mutation<
      void,
      {
        entityId: string;
        payload: {
          class_id?: string;
          group_id?: string;
        };
      }
    >({
      query: ({ entityId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/classifications/`,
          method: "DELETE",
          body: payload,
        };
      },
      invalidatesTags: ["CLASSIFICATION", "TXN_CLASSIFICATION"],
    }),
  }),
});

export const {
  useGetAllClassificationQuery,
  useCreateClassificationMutation,
  useGetTxnClassificationsQuery,
  useAddTxnClassificationMutation,
  useDeleteTxnClassificationMutation,
  useConfirmTxnClassificationMutation,
  useRejectTxnClassificationMutation,
  useBulkAssignClassificationMutation,
  useBulkConfirmAIClassificationsMutation,
  useUpdateClassificationMutation,
  useDeleteClassificationMutation,
} = classificationApi;
